import { cn } from '../../../lib/utils'
import { ReactNode } from 'react'

interface Column<T> {
  key: keyof T | string
  label: string
  render?: (row: T) => ReactNode
  className?: string
}

interface GlassTableProps<T extends Record<string, unknown>> {
  columns: Column<T>[]
  data: T[]
  onRowClick?: (row: T) => void
  emptyMessage?: string
}

export function GlassTable<T extends Record<string, unknown>>({
  columns,
  data,
  onRowClick,
  emptyMessage = 'No data',
}: GlassTableProps<T>) {
  return (
    <div className="w-full overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10 bg-white/[0.04]">
            {columns.map(col => (
              <th
                key={String(col.key)}
                className={cn(
                  'px-4 py-3 text-left font-medium text-white/50 text-xs uppercase tracking-wider',
                  col.className
                )}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-white/40">
                {emptyMessage}
              </td>
            </tr>
          )}
          {data.map((row, i) => (
            <tr
              key={(row.id as string | number | undefined) ?? i}
              onClick={() => onRowClick?.(row)}
              className={cn(
                'border-b border-white/[0.06] transition-colors',
                onRowClick && 'cursor-pointer hover:bg-white/[0.06]',
                i % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.02]'
              )}
            >
              {columns.map(col => (
                <td
                  key={String(col.key)}
                  className={cn('px-4 py-3 text-white/80', col.className)}
                >
                  {col.render
                    ? col.render(row)
                    : (row as any)[col.key] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
